/**
 * Timeline Event Utilities
 *
 * Pure functions for rendering order timeline events.
 * All icon components, color configs, and metadata formatters live here
 * so the presentation component stays clean and under 100 lines.
 */

import {
  Plus,
  Pencil,
  Trash2,
  ArrowRight,
  Truck,
  RefreshCw,
  Gauge,
  AlertTriangle,
  Activity,
  DollarSign,
  XCircle,
} from '@spcc/uikit'

import type { OrderEvent, OrderEventAction, OrderEventEntityType } from '../../../domain/types/order-timeline.types'

// ---------------------------------------------------------------------------
// Event colors by action
// ---------------------------------------------------------------------------

interface EventColors {
  dot: string       // circle background + border
  icon: string      // icon color
  badge: string     // action badge
}

const ACTION_COLORS: Record<string, EventColors> = {
  CREATED:           { dot: 'bg-emerald-50 border-emerald-300',  icon: 'text-emerald-600', badge: 'bg-emerald-100 text-emerald-700' },
  UPDATED:           { dot: 'bg-blue-50 border-blue-300',        icon: 'text-blue-600',    badge: 'bg-blue-100 text-blue-700' },
  DELETED:           { dot: 'bg-red-50 border-red-300',          icon: 'text-red-600',     badge: 'bg-red-100 text-red-700' },
  STATUS_CHANGED:    { dot: 'bg-amber-50 border-amber-300',      icon: 'text-amber-600',   badge: 'bg-amber-100 text-amber-700' },
  RESOURCE_ASSIGNED: { dot: 'bg-sky-50 border-sky-300',          icon: 'text-sky-600',     badge: 'bg-sky-100 text-sky-700' },
  RESOURCE_CHANGED:  { dot: 'bg-orange-50 border-orange-300',    icon: 'text-orange-600',  badge: 'bg-orange-100 text-orange-700' },
  WEIGHED:           { dot: 'bg-purple-50 border-purple-300',    icon: 'text-purple-600',  badge: 'bg-purple-100 text-purple-700' },
  WEIGHT_CORRECTED:  { dot: 'bg-rose-50 border-rose-300',        icon: 'text-rose-600',    badge: 'bg-rose-100 text-rose-700' },
  GATE_OPENED:       { dot: 'bg-cyan-50 border-cyan-300',        icon: 'text-cyan-600',    badge: 'bg-cyan-100 text-cyan-700' },
  WEIGHBRIDGE_TRIGGERED: { dot: 'bg-violet-50 border-violet-300', icon: 'text-violet-600', badge: 'bg-violet-100 text-violet-700' },
  KIOSK_SCAN_FAILED: { dot: 'bg-rose-50 border-rose-400',        icon: 'text-rose-600',    badge: 'bg-rose-100 text-rose-700' },
}

const DEFAULT_COLORS: EventColors = {
  dot: 'bg-slate-50 border-slate-300',
  icon: 'text-slate-500',
  badge: 'bg-slate-100 text-slate-600',
}

export function getEventColors(action: OrderEventAction | string): EventColors {
  return ACTION_COLORS[action] ?? DEFAULT_COLORS
}

// ---------------------------------------------------------------------------
// Event icons by action
// ---------------------------------------------------------------------------

const ACTION_ICONS: Record<string, typeof Activity> = {
  CREATED:           Plus,
  UPDATED:           Pencil,
  DELETED:           Trash2,
  STATUS_CHANGED:    ArrowRight,
  RESOURCE_ASSIGNED: Truck,
  RESOURCE_CHANGED:  RefreshCw,
  WEIGHED:           Gauge,
  WEIGHT_CORRECTED:  AlertTriangle,
  GATE_OPENED:       ArrowRight,
  WEIGHBRIDGE_TRIGGERED: Gauge,
  KIOSK_SCAN_FAILED: XCircle,
}

export function getEventIcon(action: OrderEventAction | string): typeof Activity {
  return ACTION_ICONS[action] ?? Activity
}

// ---------------------------------------------------------------------------
// Entity type display labels — now maps to i18n key paths
// ---------------------------------------------------------------------------

const ENTITY_TYPE_I18N_KEYS: Record<string, string> = {
  ORDER:              'orders.timelineEntityOrder',
  SHIPMENT:           'orders.timelineEntityShipment',
  QUEUE_ENTRY:        'orders.timelineEntityQueueEntry',
  WEIGHBRIDGE_TICKET: 'orders.timelineEntityWeighbridge',
  TRANSACTION:        'orders.timelineEntityTransaction',
  WEIGHT_CORRECTION:  'orders.timelineEntityWeightCorrection',
  SALES_ORDER:        'orders.timelineEntitySalesOrder',
  TENANT:             'orders.timelineEntityTenant',
  USER:               'orders.timelineEntityUser',
  CUSTOMER_ACCOUNT:   'orders.timelineEntityCustomerAccount',
  VARIANT:            'orders.timelineEntityVariant',
  SKU:                'orders.timelineEntitySku',
  CATEGORY:           'orders.timelineEntityCategory',
  LOADING_LOCATION:   'orders.timelineEntityLoadingLocation',
}

export function getEntityTypeI18nKey(entityType: OrderEventEntityType | string): string {
  return ENTITY_TYPE_I18N_KEYS[entityType] ?? 'orders.timelineEntityOrder'
}

// ---------------------------------------------------------------------------
// Action badge — maps to i18n key paths
// ---------------------------------------------------------------------------

const ACTION_I18N_KEYS: Record<string, string> = {
  CREATED:           'orders.timelineActionCreated',
  UPDATED:           'orders.timelineActionUpdated',
  DELETED:           'orders.timelineActionDeleted',
  STATUS_CHANGED:    'orders.timelineActionStatusChanged',
  RESOURCE_ASSIGNED: 'orders.timelineActionResourceAssigned',
  RESOURCE_CHANGED:  'orders.timelineActionResourceChanged',
  WEIGHED:           'orders.timelineActionWeighed',
  WEIGHT_CORRECTED:  'orders.timelineActionWeightCorrected',
  GATE_OPENED:       'orders.timelineActionGateOpened',
  WEIGHBRIDGE_TRIGGERED: 'orders.timelineActionWeighbridgeTriggered',
  APPROVED:          'orders.timelineActionApproved',
  SUSPENDED:         'orders.timelineActionSuspended',
  ACTIVATED:         'orders.timelineActionActivated',
  BLOCKED:           'orders.timelineActionBlocked',
  UNBLOCKED:         'orders.timelineActionUnblocked',
  PASSWORD_RESET:    'orders.timelineActionPasswordReset',
  ROLE_CHANGED:      'orders.timelineActionRoleChanged',
  CHANNEL_CHANGED:   'orders.timelineActionChannelChanged',
  GROUP_CHANGED:     'orders.timelineActionGroupChanged',
  KIOSK_SCAN_FAILED: 'orders.timelineActionKioskScanFailed',
}

export function getActionI18nKey(action: OrderEventAction | string): string {
  return ACTION_I18N_KEYS[action] ?? 'orders.timelineActionUpdated'
}

// ---------------------------------------------------------------------------
// Translate function type — accepts i18next t() signature subset
// ---------------------------------------------------------------------------

type TranslateFn = (key: string, options?: Record<string, unknown>) => string

const STATUS_I18N_KEYS: Record<string, string> = {
  planned: 'orders.timelineStatusPlanned',
  processing: 'orders.timelineStatusProcessing',
  delivered: 'orders.timelineStatusDelivered',
  canceled: 'orders.timelineStatusCanceled',
  cancelled: 'orders.timelineStatusCanceled',
  failed: 'orders.timelineStatusFailed',
  pending: 'orders.timelineStatusPending',
  loading: 'orders.timelineStatusLoading',
  in_transit: 'orders.timelineStatusInTransit',
  arrived: 'orders.timelineStatusArrived',
  completed: 'orders.timelineStatusCompleted',
  ready: 'orders.timelineStatusReady',
  entered: 'orders.timelineStatusEntered',
  waiting: 'orders.timelineStatusWaiting',
}

const WEIGHT_STAGE_I18N_KEYS: Record<string, string> = {
  first_weight: 'orders.timelineStageFirstWeight',
  second_weight: 'orders.timelineStageSecondWeight',
}

const FIELD_I18N_KEYS: Record<string, string> = {
  orderStatus: 'orders.timelineFieldOrderStatus',
  shipmentStatus: 'orders.timelineFieldShipmentStatus',
  sapSalesOrderNumber: 'orders.timelineFieldSapSalesOrderNumber',
  pricingSnapshot: 'orders.timelineFieldPricingSnapshot',
  processingStartedAt: 'orders.timelineFieldProcessingStartedAt',
  quantity: 'orders.timelineFieldQuantity',
  deliveryDate: 'orders.timelineFieldDeliveryDate',
  cancellationReason: 'orders.timelineFieldCancellationReason',
}

function translateKnownValue(value: unknown, map: Record<string, string>, t: TranslateFn): unknown {
  if (typeof value !== 'string') return value
  const key = map[value]
  return key ? t(key, { defaultValue: value.replace(/_/g, ' ') }) : value
}

function translateChangedFields(fields: unknown, t: TranslateFn): unknown {
  if (!Array.isArray(fields)) return fields
  return fields.map((field) => translateKnownValue(String(field), FIELD_I18N_KEYS, t))
}

function getFallbackOrderNumber(event: OrderEvent, meta: Record<string, unknown>): unknown {
  const orderNumber = meta.orderNumber
  if (typeof orderNumber === 'string' && orderNumber.trim()) return orderNumber
  if (orderNumber != null) return orderNumber
  return event.entityType === 'ORDER' ? event.entityId : orderNumber
}

function getLegacyDescriptionKey(description: string): string {
  if (description.startsWith('Gate opened for entry')) return 'orders.audit.gateOpenedForEntry'
  if (description.startsWith('Gate opened for exit')) return 'orders.audit.gateOpenedForExit'
  if (description.startsWith('Weighbridge flow triggered')) return 'orders.audit.weighbridgeFlowTriggered'
  if (description.includes(' entered factory at gate ')) return 'orders.audit.queueEntryEnteredFactory'
  if (description.includes(' exited factory')) return 'orders.audit.queueEntryExitedFactory'
  return description
}

function getLegacyGateLabel(description: string): string | null {
  const gateOpenMatch = description.match(/—\s*(.+)$/)
  if (gateOpenMatch?.[1]) return gateOpenMatch[1]
  const weighbridgeMatch = description.match(/at gate\s+(.+?)\s+\(stage:/)
  if (weighbridgeMatch?.[1]) return weighbridgeMatch[1]
  const queueMatch = description.match(/at gate\s+(.+?)\s+\(/)
  return queueMatch?.[1] ?? null
}

export function getTimelineDescription(event: OrderEvent, t: TranslateFn): string | null {
  if (event.action === 'WEIGHED') {
    return t('orders.timelineActionWeighed', { defaultValue: 'Weighed' })
  }
  if (!event.description) return null
  const descriptionKey = getLegacyDescriptionKey(event.description)
  const metadata = translateTimelineMetadata(event, t)
  return t(descriptionKey, { ...metadata, defaultValue: event.description })
}

export function translateTimelineMetadata(event: OrderEvent, t: TranslateFn): Record<string, unknown> {
  const meta = { ...(event.metadata ?? {}) }
  const descriptionKey = getLegacyDescriptionKey(event.description ?? '')
  if (meta.toStatus == null && descriptionKey === 'orders.audit.queueEntryEnteredFactory') {
    meta.toStatus = 'entered'
  }
  if (meta.toStatus == null && descriptionKey === 'orders.audit.queueEntryExitedFactory') {
    meta.toStatus = 'completed'
  }
  meta.orderNumber = getFallbackOrderNumber(event, meta)
  meta.previousStatus = translateKnownValue(meta.previousStatus, STATUS_I18N_KEYS, t)
  meta.newStatus = translateKnownValue(meta.newStatus, STATUS_I18N_KEYS, t)
  meta.fromStatus = translateKnownValue(meta.fromStatus, STATUS_I18N_KEYS, t)
  meta.toStatus = translateKnownValue(meta.toStatus, STATUS_I18N_KEYS, t)
  meta.weightStageLabel = translateKnownValue(meta.weightStage, WEIGHT_STAGE_I18N_KEYS, t)
  meta.changedFields = translateChangedFields(meta.changedFields, t)
  meta.gateLabel = meta.gateLabel ?? getLegacyGateLabel(event.description ?? '')
  return meta
}

// ---------------------------------------------------------------------------
// Metadata detail extractor — returns a concise secondary line or null
// ---------------------------------------------------------------------------

export function extractMetadataDetail(event: OrderEvent, t: TranslateFn): string | null {
  const meta = translateTimelineMetadata(event, t)
  if (!meta) return null

  if (event.action === 'RESOURCE_ASSIGNED') {
    const res = meta.resource as Record<string, unknown> | undefined
    if (!res) return null
    const driver = (res.driver as Record<string, unknown> | undefined)?.name ?? '?'
    const plate = (res.vehicle as Record<string, unknown> | undefined)?.licensePlate ?? '?'
    const trailer = (res.trailer as Record<string, unknown> | undefined)?.licensePlate
    return trailer
      ? `${t('orders.timelineMetaDriver')}: ${driver} | ${t('orders.timelineMetaVehicle')}: ${plate} | ${t('orders.timelineMetaTrailer')}: ${trailer}`
      : `${t('orders.timelineMetaDriver')}: ${driver} | ${t('orders.timelineMetaVehicle')}: ${plate}`
  }

  if (event.action === 'RESOURCE_CHANGED') {
    const prev = meta.previous as Record<string, unknown> | undefined
    const next = meta.new as Record<string, unknown> | undefined
    const prevDriver = (prev?.driver as Record<string, unknown> | undefined)?.name ?? '?'
    const nextDriver = (next?.driver as Record<string, unknown> | undefined)?.name ?? '?'
    const prevPlate = (prev?.vehicle as Record<string, unknown> | undefined)?.licensePlate ?? '?'
    const nextPlate = (next?.vehicle as Record<string, unknown> | undefined)?.licensePlate ?? '?'
    return `${prevDriver} (${prevPlate}) → ${nextDriver} (${nextPlate})`
  }

  if (event.action === 'WEIGHED') {
    const parts: string[] = []
    const firstWeight = meta.firstWeight ?? meta.tareWeight
    const secondWeight = meta.secondWeight ?? meta.grossWeight
    if (firstWeight != null) parts.push(`${t('orders.timelineMetaFirst')}: ${Number(firstWeight).toLocaleString()} kg`)
    if (secondWeight != null) parts.push(`${t('orders.timelineMetaSecond')}: ${Number(secondWeight).toLocaleString()} kg`)
    if (meta.netWeight != null) parts.push(`${t('orders.timelineMetaNet')}: ${Number(meta.netWeight).toLocaleString()} kg`)
    return parts.length > 0 ? parts.join(' | ') : null
  }

  if (event.action === 'WEIGHT_CORRECTED') {
    const prevNet = (meta.previousValues as Record<string, unknown> | undefined)?.netWeight
    const newNet = (meta.newValues as Record<string, unknown> | undefined)?.netWeight
    if (prevNet != null && newNet != null) {
      return `${t('orders.timelineMetaNetWeight')}: ${Number(prevNet).toLocaleString()} → ${Number(newNet).toLocaleString()} kg`
    }
    if (meta.reason) return `${t('orders.timelineMetaReason')}: ${meta.reason}`
    return null
  }

  if (event.action === 'STATUS_CHANGED' && meta.forced) {
    const parts: string[] = []
    if (meta.forcedBy) parts.push(`${t('orders.timelineMetaForcedBy')}: ${meta.forcedBy}`)
    if (meta.reason) parts.push(`${t('orders.timelineMetaReason')}: ${meta.reason}`)
    if (meta.incidentRef) parts.push(`${t('orders.timelineMetaIncident')}: ${meta.incidentRef}`)
    if (meta.affectedOpsNote) parts.push(`${t('orders.timelineMetaOpsNote')}: ${meta.affectedOpsNote}`)
    return parts.length > 0 ? parts.join(' | ') : null
  }

  if (event.action === 'UPDATED' && Array.isArray(meta.changedFields)) {
    return `${t('orders.timelineMetaChanged')}: ${(meta.changedFields as string[]).join(', ')}`
  }

  if (event.action === 'KIOSK_SCAN_FAILED') {
    const errorCode = meta.errorCode as string | undefined
    if (!errorCode) return null
    const reasonKey = `orders.audit.kioskErrors.${errorCode}`
    const reason = t(reasonKey, { defaultValue: errorCode })
    return `${t('orders.timelineMetaReason')}: ${reason}`
  }

  return null
}

// ---------------------------------------------------------------------------
// Time formatters
// ---------------------------------------------------------------------------

function padTwoDigits(value: number): string {
  return value.toString().padStart(2, '0')
}

export function formatRelativeTime(dateStr: string, locale?: string): string {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return ''

  const activeLocale = locale || undefined
  const time = new Intl.DateTimeFormat(activeLocale, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
  const day = padTwoDigits(d.getDate())
  const month = padTwoDigits(d.getMonth() + 1)
  const year = d.getFullYear()

  return `${time} - ${day}/${month}/${year}`
}

export function formatAbsoluteTime(dateStr: string, locale?: string): string {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString(locale || undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
